import { Modal } from "react-bootstrap";
import { Button } from "./Controls/Button";
import {
  InfoCircle,
  Calendar3,
  CreditCard2Back,
  ArrowRepeat,
  Receipt,
  ExclamationTriangle,
} from "react-bootstrap-icons";

type Props = {
  show: boolean;
  onHide: () => void;
  isAccountsReceivable: boolean;
};

export function BillingInfoModal({ show, onHide, isAccountsReceivable }: Props) {
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <InfoCircle className="text-primary" />
          How Billing Works
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2">
        {isAccountsReceivable ? (
          <InvoicedPlanInfo />
        ) : (
          <AutoPayPlanInfo />
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button className="btn-main" onClick={onHide}>
          Got it
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function InvoicedPlanInfo() {
  return (
    <div className="d-flex flex-column gap-4">
      <div className="bg-primary-subtle rounded p-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <Receipt className="text-primary fs-5" />
          <span className="fw-semibold">This Plan is Billed by Invoice</span>
        </div>
        <p className="mb-0 text-muted small">
          The parker receives an invoice for each billing period. Here's how changes to the plan are handled:
        </p>
      </div>

      <div className="d-flex flex-column gap-3">
        <BillingScenario
          icon={<Calendar3 className="text-warning" />}
          title="Changes Before Payment"
          description="If changes are made to the plan before the billing period starts and before the invoice has been paid, an amended invoice will be sent reflecting the updated charges."
          highlight="warning"
        />

        <BillingScenario
          icon={<CreditCard2Back className="text-success" />}
          title="Changes After Payment"
          description="Any changes made after the invoice has been paid will be calculated and included on the next month's invoice."
          highlight="success"
        />

        <BillingScenario
          icon={<ArrowRepeat className="text-info" />}
          title="Ongoing Billing"
          description="The billing cycle continues each month. The Cost Projections show estimated charges for the current and upcoming periods based on the current plan configuration."
          highlight="info"
        />
      </div>

      <div className="bg-warning-subtle rounded p-3 mt-2 d-flex align-items-start gap-2">
        <ExclamationTriangle className="text-warning flex-shrink-0 mt-1" />
        <div>
          <div className="fw-semibold mb-1">Important Note</div>
          <p className="mb-0 small text-muted">
            If the parker switches to auto-pay, any outstanding invoice balances must be settled separately. Adding a payment method does not automatically pay previous invoices.
          </p>
        </div>
      </div>

      <div className="bg-light rounded p-3 mt-2">
        <div className="fw-semibold mb-1">Quick Summary</div>
        <ul className="mb-0 small text-muted ps-3">
          <li>Before payment: Amended invoice sent</li>
          <li>After payment: Adjustments on next invoice</li>
          <li>Payment method added: Converts to auto-pay</li>
        </ul>
      </div>
    </div>
  );
}

function AutoPayPlanInfo() {
  return (
    <div className="d-flex flex-column gap-4">
      <div className="bg-primary-subtle rounded p-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          <CreditCard2Back className="text-primary fs-5" />
          <span className="fw-semibold">This Plan Renews Automatically</span>
        </div>
        <p className="mb-0 text-muted small">
          The parker's payment method is charged automatically at each renewal. Here's how changes to the plan are handled:
        </p>
      </div>

      <div className="d-flex flex-column gap-3">
        <BillingScenario
          icon={<Calendar3 className="text-warning" />}
          title="Changes During Current Period"
          description="Any changes made after the current billing period has started (such as adding or removing parkers) will not affect the current charge. These changes will be reflected in the next renewal."
          highlight="warning"
        />

        <BillingScenario
          icon={<ArrowRepeat className="text-success" />}
          title="Next Renewal"
          description="When the plan renews, the payment method will be charged based on the plan configuration at that time, including any changes made during the current period."
          highlight="success"
        />
      </div>

      <div className="bg-light rounded p-3 mt-2">
        <div className="fw-semibold mb-1">Quick Summary</div>
        <ul className="mb-0 small text-muted ps-3">
          <li>Current period is already set at the start of the billing cycle</li>
          <li>Changes made now: Included in next month's renewal</li>
        </ul>
      </div>
    </div>
  );
}

type BillingScenarioProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  highlight: "warning" | "success" | "info";
};

function BillingScenario({
  icon,
  title,
  description,
  highlight,
}: BillingScenarioProps) {
  const borderClass = `border-start border-${highlight} border-3`;

  return (
    <div className={`${borderClass} ps-3 py-1`}>
      <div className="d-flex align-items-center gap-2 mb-1">
        {icon}
        <span className="fw-semibold">{title}</span>
      </div>
      <p className="mb-0 small text-muted">{description}</p>
    </div>
  );
}
